import { useEffect, useRef, useState } from 'react'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'

type Props = {
  onFiles: (files: File[]) => void
  children: React.ReactNode
}

// Window-level drag-drop wrapper. Shows an overlay while the user is dragging
// files over the window and forwards dropped files to the handler.
export default function FileDropZone({ onFiles, children }: Props) {
  const [active, setActive] = useState(false)
  // Counter keeps the overlay stable as drags move between child elements
  // (dragenter/leave fire on every crossing).
  const counter = useRef(0)
  // Ref avoids re-registering all four window listeners when onFiles changes.
  const onFilesRef = useRef(onFiles)
  onFilesRef.current = onFiles

  useEffect(() => {
    const hasFiles = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files')

    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      counter.current++
      setActive(true)
    }
    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
    }
    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return
      counter.current = Math.max(0, counter.current - 1)
      if (counter.current === 0) setActive(false)
    }
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      counter.current = 0
      setActive(false)
      const files = Array.from(e.dataTransfer?.files ?? [])
      if (files.length) onFilesRef.current(files)
    }

    window.addEventListener('dragenter', onDragEnter)
    window.addEventListener('dragover', onDragOver)
    window.addEventListener('dragleave', onDragLeave)
    window.addEventListener('drop', onDrop)
    return () => {
      window.removeEventListener('dragenter', onDragEnter)
      window.removeEventListener('dragover', onDragOver)
      window.removeEventListener('dragleave', onDragLeave)
      window.removeEventListener('drop', onDrop)
    }
  }, [])

  return (
    <>
      {children}
      {active && (
        <Box
          sx={({ shape, palette, zIndex, spacing }) => ({
            position: 'fixed',
            inset: spacing(8, 1, 2, 1),
            zIndex: zIndex.modal,
            background: palette.contrast.paper,
            opacity: 0.8,
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: `${shape.borderRadiusLarge}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Typography variant="h5" color="primary" sx={{
            fontWeight: 600
          }}>
            Drop files to upload
          </Typography>
        </Box>
      )}
    </>
  )
}
